import type { Dispatch, SetStateAction } from 'react';

export interface ScaleValues {
  current: number;
  min: number;
  max: number;
}

export interface ImageDragPositions {
  x: number;
  y: number;
  cursorX: number;
  cursorY: number;
}

export interface PageState {
  isLoaded: boolean;
  width: number;
  height: number;
}

export interface LightboxFile {
  url: string;
  openInNewTabUrl: string;
  name: string;
  type: 'document' | 'image';
}

export enum STATUS {
  closed = 'closed',
  opening = 'opening',
  open = 'open',
  closing = 'closing',
}

export interface OpenLightboxOptions {
  fileUrl: string;
  fileOpenInNewTabUrl: string;
  fileName: string;
  fileSizeBytes?: number;
  /**
   * To force the lightbox to show the file, set this flag to `true`.
   *
   * Otherwise, whether the file is supported for lightbox preview will be
   * determined automatically based on the file size and its extension.
   *
   * Single files will only be shown directly if they are supported,
   * otherwise they will be opened in a new tab.
   * Multiple files will be shown only if at least one file is supported,
   * and if that is the case, only the supported ones will be shown.
   */
  isSupported?: boolean;
  title?: string;
  shouldHideDownload?: boolean;
  shouldHideOpenInNewTab?: boolean;
  initialScale?: ScaleValues;
  onUnsupported?: () => void;
  onOpened?: () => void;
}

export interface Lightbox {
  file: LightboxFile | null;
  images: Pick<LightboxFile, 'url' | 'name'>[] | null;
  title: string | null;
  shouldHideDownload: boolean;
  shouldHideOpenInNewTab: boolean;
  initialScale: ScaleValues | null;
  isLoading: boolean;
  status: STATUS;
  open: (options: OpenLightboxOptions) => void;
  openImages: (options: OpenLightboxOptions[], index?: number) => void;
  close: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setStatus: Dispatch<SetStateAction<STATUS>>;
}
